<template>
    <pc-input
        v-if="slotSchema.IsSignal"
        v-model="memberName"
        v-bind:disabled="!slotSchema.IsEditable"
        v-bind:label="slotSchema.Name"
        v-bind:required="slotSchema.IsRequired"
        v-bind:readonly="!canEditMember"
        class="iw-4"
        v-bind:maxlength="255"
        v-bind:validator="validator" />
    <pc-select
        v-else
        v-model="memberMappedTo"
        v-bind:label="slotSchema.Name"
        v-bind:disabled="!slotSchema.IsEditable"
        v-bind:options="getMappingOptions(slotSchema.SlotType, member.DataType, allowReadOnlyOptions)"
        v-bind:required="slotSchema.IsRequired"
        v-bind:validator="validator"
        value-prop="PublicId"
        text-prop="Name"
        class="iw-4">
        <option
            v-if="!slotSchema.IsRequired"
            value="">
            {{ noneSelectedOptionLabel }}
        </option>
    </pc-select>
</template>

<script setup>
    import { computed, inject } from "vue";
    import { storeToRefs } from "pinia";
    import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";
    import { useChannelStore } from "./channelStore.js";
    import { DIRECTION_TYPES, NEW_MEMBER_ID_BASE } from "Types/channelTypes.js";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { KEYS, NAMESPACE } from "../i18n/programChannels.i18n";
    import { useResource } from "Util/i18n";

    const app = inject(PAGE_APP_SYMBOL);
    const channelStore = useChannelStore();
    const { isPublishing, channelInUse } = storeToRefs(channelStore);
    const { setMemberMappedTo, setMemberName, hasValidMemberName, getMappingOptions } = channelStore;

    const { noneSelectedOptionLabel, duplicateNameMessage } = useResource(KEYS, NAMESPACE);

    const props = defineProps({
        slotSchema: { type: Object, required: true },
        member: { type: Object, required: true }
    });

    const canEditMember = computed(() => {
        return props.member.ConnectionMemberId.startsWith(NEW_MEMBER_ID_BASE) || !channelInUse.value;
    });

    const validator = computed(() => {
        const validators = [];
        if (props.slotSchema.IsRequired) {
            validators.push(VALIDATOR_TYPES.required);
        }
        if (props.slotSchema.IsSignal) {
            validators.push({
                type: VALIDATOR_TYPES.custom,
                validate: () => hasValidMemberName(props.member),
                errorMsg: duplicateNameMessage.value
            });
        }
        return getValidator(app, validators);
    });

    const memberName = computed({
        get: () => props.member.Name,
        set(value) {
            app.setUnsavedChanges(true);
            setMemberName(props.member.ConnectionMemberId, value);
        }
    });

    const memberMappedTo = computed({
        get: () => props.member.MappedTo,
        set(value) {
            if (value !== props.member.MappedTo) {
                app.setUnsavedChanges(true);
                setMemberMappedTo(props.member.ConnectionMemberId, value);
            }
        }
    });

    const allowReadOnlyOptions = computed(() => {
        // If we are copying from this program then allow readonly options
        return (
            (isPublishing.value && props.slotSchema.Direction === DIRECTION_TYPES.FromPublishingProgram) ||
            (!isPublishing.value && props.slotSchema.Direction === DIRECTION_TYPES.FromConsumingProgram)
        );
    });
</script>
